import {
  BulkCSVUpdateJob,
  BulkCSVUpdateJobResult,
  BulkCSVUpdateJobStatus,
  BulkCSVUpdateMapping,
  BulkCSVUpdateMappings,
} from 'models';

/**
 * Create a BulkCSVUpdateMapping object from its API equivalent.
 *
 * @param apiBulkCSVUpdateMapping API Bulk CSV update mapping object.
 * @returns A BulkCSVUpdateMapping object.
 */
export const createBulkCSVUpdateMappingFromApi = (
  apiBulkCSVUpdateMapping: S9ApiBulkCSVUpdateMapping
): BulkCSVUpdateMapping => ({
  displayName: apiBulkCSVUpdateMapping.DisplayName,
  includeField: apiBulkCSVUpdateMapping.IncludeField,
  isFilepath: apiBulkCSVUpdateMapping.IsFilePath,
  fieldId: apiBulkCSVUpdateMapping.FieldId,
  columnIndex: apiBulkCSVUpdateMapping.ColumnIndex,
});

export interface S9ApiBulkCSVUpdateJobDefinition {
  ArchiveIds: number[];
  DatabaseId: string;
  Documents: S9ApiBulkCSVUpdateDocumentDefinition[];
  Id: string;
  Status: S9ApiBulkCSVUpdateStatusDefinition;
}

export interface S9ApiBulkCSVUpdateDocumentDefinition {
  Id: number;
  ArchiveId: number;
}

/**
 * Create a BulkCSVUpdateMappings object from its API equivalent.
 *
 * @param apiBulkCSVUpdateMappings API bulk CSV update mapping objects.
 * @returns A BulkCSVUpdateMappings object.
 */
export const createBulkCSVUpdateMappingsFromApi = (
  apiBulkCSVUpdateMappings: S9ApiBulkCSVUpdateMappings
): BulkCSVUpdateMappings =>
  apiBulkCSVUpdateMappings.map(createBulkCSVUpdateMappingFromApi);

/**
 * Create a BulkCSVUpdateJob object from its API equivalent.
 *
 * @param apiJob API job object.
 * @returns A Bulk CSV update job.
 */
export const createBulkCSVUpdateJobFromApi = (
  apiJob: S9APIBulkCSVUpdateJob
): BulkCSVUpdateJob => ({
  id: apiJob.Id,
  archiveId: apiJob.ArchiveId,
  databaseId: apiJob.DatabaseId,
  status: createBulkCSVUpdateStatusFromApi(apiJob.Status),
  columnDelimiter: apiJob.ColumnDelimiter,
  stringDelimiter: apiJob.StringDelimiter,
  containsColumnHeaders: apiJob.ContainsColumnHeaders,
  columnMappings: apiJob.ColumnMappings
    ? createBulkCSVUpdateMappingsFromApi(apiJob.ColumnMappings)
    : undefined,
  startTime: new Date(apiJob.StartTime),
});

/**
 * Create an BulkCSVUpdateJobStatus object from its API equivalent.
 *
 * @param apiStatus API status object.
 * @returns A BulkCSVUpdateJobStatus object.
 */
export const createBulkCSVUpdateStatusFromApi = (
  apiStatus: S9ApiBulkCSVUpdateJobStatus
): BulkCSVUpdateJobStatus => ({
  totalRows: apiStatus.TotalRows,
  updatedRows: apiStatus.UpdatedRows,
  erroredRows: apiStatus.ErroredRows,
  currentRow: apiStatus.CurrentRow,
  progress: apiStatus.Progress,
  started: apiStatus.Started,
  done: apiStatus.Done,
  cancelled: apiStatus.Cancelled,
});

/**
 * Create a BulkCSVUpdateJobResult object from its API equivalent.
 *
 * @param apiResult API result object.
 * @returns A BulkCSVUpdateJobResult object.
 */
export const createBulkCSVUpdateResultFromApi = (
  apiResult: S9APIBulkCSVUpdateJobResult
): BulkCSVUpdateJobResult => ({
  errorMessages: apiResult.ErrorMessages,
  erroredRows: apiResult.ErroredRows,
  updatedRows: apiResult.UpdatedRows,
  totalRows: apiResult.TotalRows,
});

export interface S9ApiBulkCSVUpdateMapping {
  DisplayName: string;
  IncludeField: boolean;
  IsFilePath: boolean;
  FieldId: number;
  ColumnIndex: number;
}

export interface S9APIBulkCSVUpdateJobStatus {
  TotalRows: number;
  UpdatedRows: number;
  ErroredRows: number;
  CurrentRow: number;
  Progress: number;
  Started: boolean;
  Done: boolean;
}

/**
 * An Array of S9ApiBulkCSVUpdateMapping objects.
 *
 * @see S9ApiBulkCSVUpdateMapping
 */
export declare type S9ApiBulkCSVUpdateMappings = S9ApiBulkCSVUpdateMapping[];

export interface S9APIBulkCSVUpdateJob {
  Id: string;
  ArchiveId: number;
  DatabaseId: number;
  Status: S9ApiBulkCSVUpdateJobStatus;
  JobPath: string;
  CsvFilepath: string;
  ColumnDelimiter: string;
  StringDelimiter: string;
  ContainsColumnHeaders: boolean;
  ColumnMappings: S9ApiBulkCSVUpdateMappings;
  IdentityHolder: BulkCSVIdentityHolder;
  StartTime: Date;
}

export interface BulkCSVIdentityHolder {
  UserWithDomain: string;
  Roles: string[];
}

export interface S9ApiBulkCSVUpdateJobStatus {
  TotalRows: number;
  UpdatedRows: number;
  ErroredRows: number;
  CurrentRow: number;
  Progress: number;
  Started: boolean;
  Done: boolean;
  Cancelled: boolean;
}

export interface S9APIBulkCSVUpdateJobResult {
  TotalRows: number;
  UpdatedRows: number;
  ErroredRows: number;
  ErrorMessages: string[];
}

export interface S9ApiBulkCSVUpdateStatusDefinition {
  TotalRows: number;
  Cancelled: boolean;
  CurrentRow: number;
  Done: boolean;
  ErroredRows: number;
  UpdatedRows: number;
  Progress: number;
  Started: boolean;
}
